import { jsx as _jsx } from "react/jsx-runtime";
import Cookies from 'js-cookie';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import isBrowser from '../helpers/is-browser';
import { useParams } from './pagination-provider/params-provider';
import { usePlatform } from './platform-provider';
const JOURNEY_KEYS = {
    utm_source: 'utm_source',
    utm_medium: 'utm_medium',
    utm_campaign: 'utm_campaign',
    utm_term: 'utm_term',
    utm_content: 'utm_content',
    utm_id: 'utm_id',
    ga_client_id: 'ga_client_id',
    ga_session_id: 'ga_session_id',
    campaignid: 'campaignid',
    adgroupid: 'adgroupid',
    kw: 'kw',
    gclid: 'gclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    gad_source: 'gad_source',
    glcid: 'glcid',
    fbclid: 'fbclid',
    mixpanel_distinct_id: 'mixpanel_distinct_id',
    mp_source: 'mp_source',
    hssc: 'hssc',
    hssrc: 'hssrc',
    hscts: 'hscts',
    __hstc: '__hstc',
    hubspotutk: 'hubspotutk',
    login_source: 'login_source',
    signup_source: 'signup_source',
    website_first_url: 'website_first_url',
    website_last_url: 'website_last_url',
    website_conversion_url: 'website_conversion_url',
    website_conversion_event: 'website_conversion_event',
    app_first_url: 'app_first_url',
    app_last_url: 'app_last_url',
    app_conversion_url: 'app_conversion_url',
    app_conversion_event: 'app_conversion_event',
    referrer: 'referrer',
    referral_code: 'referral_code',
    content_cta: 'content_cta',
    source_platform: 'source_platform',
    user_segment: 'user_segment',
    conversion_event: 'conversion_event',
    funnel_stage: 'funnel_stage',
};
const URL_UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
];
const ENV_CONFIG = (() => {
    const config = {
        isProduction: false,
        isStaging: false,
        cookieDomain: undefined,
        currentHref: '',
        currentHrefWithoutParams: '',
    };
    if (isBrowser) {
        const hostname = window.location.hostname;
        config.isProduction = hostname.endsWith('hellodarwin.com');
        config.isStaging = hostname.endsWith('develop.darwinrevolution.com');
        config.currentHref = window.location.href;
        const queryIndex = config.currentHref.indexOf('?');
        config.currentHrefWithoutParams =
            queryIndex !== -1
                ? config.currentHref.substring(0, queryIndex)
                : config.currentHref;
        if (config.isProduction) {
            config.cookieDomain = '.hellodarwin.com';
        }
        else if (config.isStaging) {
            config.cookieDomain = '.develop.darwinrevolution.com';
        }
    }
    return config;
})();
const DEFAULT_COOKIE_OPTIONS = {
    domain: ENV_CONFIG.cookieDomain,
    expires: 90,
    sameSite: 'lax',
};
const AnalyticContext = createContext({
    setJourneyData: () => { },
    getJourneyData: () => undefined,
    journeyData: {},
    trackConversion: () => {
        return {
            conversionUrl: '',
            platform: '',
        };
    },
    buildRedirectUrl: () => '',
    loaded: false,
});
AnalyticContext.displayName = 'AnalyticContext';
export const useAnalytics = () => {
    return useContext(AnalyticContext);
};
export const AnalyticsProvider = ({ children, }) => {
    const { app } = usePlatform();
    const { params } = useParams();
    const lastUpdateTimeRef = useRef({});
    const [journeyData, setData] = useState({});
    const [loaded, setLoaded] = useState(false);
    const syncJourneyData = useCallback(() => {
        if (!isBrowser)
            return;
        const journeyData = Object.keys(JOURNEY_KEYS).reduce((acc, key) => {
            const value = localStorage.getItem(key) || Cookies.get(key);
            if (value) {
                acc[key] = value;
            }
            return acc;
        }, {});
        setData(journeyData);
        setLoaded(true);
    }, []);
    useEffect(() => {
        if (!isBrowser)
            return;
        syncJourneyData();
    }, [syncJourneyData]);
    const setJourneyData = useCallback((key, value) => {
        if (!isBrowser)
            return;
        if (journeyData[key] === value)
            return;
        const now = Date.now();
        const lastUpdate = lastUpdateTimeRef.current[key] || 0;
        if (now - lastUpdate < 1000 && key.includes('_last_url')) {
            return;
        }
        lastUpdateTimeRef.current[key] = now;
        try {
            Cookies.set(key, value, DEFAULT_COOKIE_OPTIONS);
            localStorage.setItem(key, value);
            setData((prev) => (Object.assign(Object.assign({}, prev), { [key]: value })));
        }
        catch (error) {
            console.error(`Failed to set journey data for ${key}:`, error);
        }
    }, [journeyData]);
    useEffect(() => {
        if (!loaded)
            return;
        const currentHref = ENV_CONFIG.currentHrefWithoutParams;
        if (app === 'website') {
            if (!journeyData.website_first_url) {
                setJourneyData('website_first_url', currentHref);
            }
            if (!journeyData.utm_medium) {
                setJourneyData('utm_medium', 'website');
            }
            setJourneyData('website_last_url', currentHref);
        }
        else {
            if (!journeyData.app_first_url) {
                setJourneyData('app_first_url', currentHref);
            }
            if (!journeyData.utm_medium) {
                setJourneyData('utm_medium', 'client-app');
            }
            setJourneyData('app_last_url', currentHref);
        }
        if (!journeyData.utm_source) {
            setJourneyData('utm_source', 'helloDarwin');
        }
        if (!journeyData.source_platform) {
            setJourneyData('source_platform', app);
        }
    }, [app, journeyData, loaded, setJourneyData]);
    const getJourneyData = useCallback((key) => {
        if (!isBrowser)
            return undefined;
        try {
            return localStorage.getItem(key) || Cookies.get(key) || undefined;
        }
        catch (error) {
            console.error(`Failed to get journey data for ${key}:`, error);
            return undefined;
        }
    }, []);
    useEffect(() => {
        if (!isBrowser || Object.keys(params).length === 0)
            return;
        const batchUpdates = [];
        Object.keys(JOURNEY_KEYS).forEach((key) => {
            const typedKey = key;
            if (params[key] &&
                (URL_UTM_KEYS.includes(typedKey) || !getJourneyData(typedKey))) {
                batchUpdates.push([typedKey, params[key]]);
            }
        });
        if ((batchUpdates === null || batchUpdates === void 0 ? void 0 : batchUpdates.length) > 0) {
            batchUpdates.forEach(([key, value]) => {
                setJourneyData(key, value);
            });
        }
    }, [params, getJourneyData, setJourneyData]);
    const trackConversion = useCallback((conversionType, additionalData) => {
        if (!isBrowser)
            return;
        try {
            const conversionUrl = window.location.href;
            const urlKey = app === 'website'
                ? JOURNEY_KEYS.website_conversion_url
                : JOURNEY_KEYS.app_conversion_url;
            setJourneyData(urlKey, conversionUrl);
            setJourneyData((app === 'website'
                ? JOURNEY_KEYS.website_conversion_event
                : JOURNEY_KEYS.app_conversion_event), conversionType);
            return Object.assign({ conversionUrl, platform: app }, additionalData);
        }
        catch (error) {
            console.error(`Failed to track conversion ${conversionType}:`, error);
        }
    }, [app, setJourneyData]);
    const buildRedirectUrl = useCallback((targetPath) => {
        const [basePath, existingQueryString] = targetPath.split('?');
        const params = new URLSearchParams(existingQueryString || '');
        params.append('source_platform', app);
        [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_term',
            'utm_content',
            app === 'website' ? 'website_conversion_event' : 'app_conversion_event',
            'funnel_stage',
            'conversion_event',
        ].forEach((param) => {
            const value = getJourneyData(param);
            if (value)
                params.append(param, value);
        });
        const queryString = params.toString();
        return queryString ? `${basePath}?${queryString}` : basePath;
    }, [app, getJourneyData]);
    const contextValue = useMemo(() => ({
        setJourneyData,
        getJourneyData,
        journeyData,
        trackConversion,
        buildRedirectUrl,
        loaded,
    }), [
        setJourneyData,
        getJourneyData,
        journeyData,
        trackConversion,
        buildRedirectUrl,
        loaded,
    ]);
    return (_jsx(AnalyticContext.Provider, { value: contextValue, children: children }));
};
